import React, { useState, useEffect, useCallback } from 'react';
import { Button, DateInput, SelectInput, Text, TextInput, StatusBadge } from '@/ui-kits';
import { useSelector, useDispatch } from 'react-redux';
import {
  patientAccountTicketsSelector,
  patientAccountTicketsCurrentPageSelector,
  patientAccountTicketsTotalPagesSelector,
  patientAccountTicketsTotalResultsSelector,
  patientAdminStatus,
  patientAdminFailureSelector,
  patientAdminSuccessSelector,
} from '@/services/redux/selectors';
import {
  getPatientAccountTicketsRequest,
  resetPatientAdminMessage,
  exportPatientAccountRequest,
  deletePatientAccountRequest,
  holdPatientAccountDeletionRequest,
  resumePatientAccountDeletionRequest,
} from '@/services/redux/slices';
import { ErrorCodes, getErrorMessage } from '@/utils/getMessages';
import { useToast, useTemporaryVisibility } from '@/hooks';
import {
  RequestDetailsModal,
  ApprovedRequestDetailsModal,
  CancelledRequestDetailsModal,
  DeletedRequestDetailsModal,
  PendingRequestDetailsModal,
  PausedRequestDetailsModal,
  ResumedRequestDetailsModal,
  ExportedRequestDetailsModal,
  TemporaryVisibilityCell,
  DataTable,
} from '@/components';
import { DataTableColumn, PatientAccountTicket, GetPatientAccountTicketsRequestPayload, SelectInputitem } from '@/interface';
import { formatName, formatDate } from '@/utils/common_functions';
import { PatientAccountTicketRequestStatus, PatientAccountTicketType } from '@/constants';
import { PatientAccountRequestRequestTypeLabels, PatientAccountTicketRequestStatusBadgeVariants } from '@/constants/defaults';
import { decryptInline } from '@/utils/encryption';

// Status filter options
const StatusFilterOptions: SelectInputitem[] = [
  { id: PatientAccountTicketRequestStatus.Exported, label: 'Exported' },
  { id: PatientAccountTicketRequestStatus.Pending, label: 'Pending' },
  { id: PatientAccountTicketRequestStatus.Approved, label: 'Approved' },
  { id: PatientAccountTicketRequestStatus.Paused, label: 'Paused' },
  { id: PatientAccountTicketRequestStatus.Resumed, label: 'Resumed' },
  { id: PatientAccountTicketRequestStatus.Deleted, label: 'Deleted' },
  { id: PatientAccountTicketRequestStatus.Cancelled, label: 'Cancelled' },
];

// Type filter options
const TypeFilterOptions: SelectInputitem[] = [
  { id: PatientAccountTicketType.ExportOnly, label: 'Export Only' },
  { id: PatientAccountTicketType.ExportDelete, label: 'Export + Delete' },
];

export const PatientSettingsScreen: React.FC = () => {
  // ==========================
  // hooks
  // ==========================
  const { showToast } = useToast();
  const dispatch = useDispatch();

  // ==========================
  // selectors
  // ==========================
  const tickets = useSelector(patientAccountTicketsSelector);
  const currentPage = useSelector(patientAccountTicketsCurrentPageSelector);
  const totalPages = useSelector(patientAccountTicketsTotalPagesSelector);
  const totalResults = useSelector(patientAccountTicketsTotalResultsSelector);
  const failure = useSelector(patientAdminFailureSelector) as ErrorCodes;
  const success = useSelector(patientAdminSuccessSelector);
  const isLoading = useSelector(patientAdminStatus);

  // ===========================
  // states
  // ===========================
  const [search, setSearch] = useState<string>('');
  const [status, setStatus] = useState<number | null>(null);
  const [type, setType] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [date, setDate] = useState<Date | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<PatientAccountTicket | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isVisible: isEmailVisible, toggleVisibility: toggleEmailVisibility } = useTemporaryVisibility({ duration: 5000 });
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAddingHold, setIsAddingHold] = useState(false);
  const [isRemovingHold, setIsRemovingHold] = useState(false);

  // ===========================
  // functions
  // ===========================
  const resetFilter = () => {
    setSearch('');
    setStatus(null);
    setType(null);
    setPage(1);
    setDate(null);
  };

  const handleViewTicket = (ticket: PatientAccountTicket) => {
    // Open modal if status is "Requested", "Exported", "Pending", "Approved", "Paused", "Resumed", "Deleted", or "Cancelled"
    if (
      ticket.status.id === PatientAccountTicketRequestStatus.Requested ||
      ticket.status.id === PatientAccountTicketRequestStatus.Exported ||
      ticket.status.id === PatientAccountTicketRequestStatus.Pending ||
      ticket.status.id === PatientAccountTicketRequestStatus.Approved ||
      ticket.status.id === PatientAccountTicketRequestStatus.Paused ||
      ticket.status.id === PatientAccountTicketRequestStatus.Resumed ||
      ticket.status.id === PatientAccountTicketRequestStatus.Deleted ||
      ticket.status.id === PatientAccountTicketRequestStatus.Cancelled
    ) {
      setSelectedTicket(ticket);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTicket(null);
  };

  const handleExport = () => {
    if (selectedTicket?.id) {
      setIsExporting(true);
      dispatch(exportPatientAccountRequest({ id: selectedTicket.id }));
    }
  };

  const handleFinalDeletion = () => {
    if (selectedTicket?.id) {
      setIsDeleting(true);
      dispatch(deletePatientAccountRequest({ id: selectedTicket.id }));
    }
  };

  const handleApproveDeletion = () => {
    if (selectedTicket?.id) {
      setIsDeleting(true);
      dispatch(deletePatientAccountRequest({ id: selectedTicket.id }));
    }
  };

  const handleAddComplianceHold = () => {
    if (selectedTicket?.id) {
      setIsAddingHold(true);
      dispatch(holdPatientAccountDeletionRequest({ id: selectedTicket.id }));
    }
  };

  const handleRemoveHold = () => {
    if (selectedTicket?.id) {
      setIsRemovingHold(true);
      dispatch(resumePatientAccountDeletionRequest({ id: selectedTicket.id }));
    }
  };


  // Define columns with access to handler
  const columns: DataTableColumn<PatientAccountTicket>[] = [
    {
      name: 'patient id',
      align: 'center',
      render: row => (
        <Text variant="body" className="text-ui_input_border_dark">
          {row.patient.id}
        </Text>
      ),
      title: <Text>PATIENT ID</Text>,
      width: { minWidth: '140px', maxWidth: '150px' },
    },
    {
      name: 'patient name',
      align: 'left',
      render: row => (
        <Text variant="body" className="text-ui_input_border_dark">
          {formatName(row.patient.name)}
        </Text>
      ),
      title: <Text>PATIENT NAME</Text>,
      width: { minWidth: '180px', maxWidth: '250px' },
    },
    {
      name: 'email',
      align: 'center',
      render: row => (
        <TemporaryVisibilityCell
          id={row.id}
          value={row.patient.email}
          isVisible={isEmailVisible(row.id)}
          onToggle={toggleEmailVisibility}
          valueFormatter={decryptInline}
        />
      ),
      title: <Text>EMAIL</Text>,
      width: { minWidth: '300px', maxWidth: '300px' },
    },
    {
      name: 'request type',
      align: 'left',
      render: row => (
        <Text variant="body" className="text-ui_input_border_dark">
          {PatientAccountRequestRequestTypeLabels[row.type.id as PatientAccountTicketType]}
        </Text>
      ),
      title: <Text>REQUEST TYPE</Text>,
      width: { minWidth: '180px', maxWidth: '200px' },
    },
    {
      name: 'requested on',
      align: 'center',
      render: row => (
        <Text variant="body" className="text-ui_input_border_dark">
          {formatDate(row.created_at)}
        </Text>
      ),
      title: <Text>REQUESTED ON</Text>,
      width: { minWidth: '200px', maxWidth: '180px' },
    },
    {
      name: 'status',
      align: 'center',
      render: row => (
        <StatusBadge variant="secondary" status={PatientAccountTicketRequestStatusBadgeVariants[row.status.id as PatientAccountTicketRequestStatus]}>
          {row.status.label}
        </StatusBadge>
      ),
      title: <Text>STATUS</Text>,
      width: { minWidth: '170px', maxWidth: '150px' },
    },
    {
      name: 'actions',
      align: 'center',
      render: row => (
        <button onClick={() => handleViewTicket(row)} className="text-ui_blue hover:underline">
          <Text variant="body" className="text-ui_blue_light font-FigtreeSemiBold">
            View
          </Text>
        </button>
      ),
      title: <Text>ACTIONS</Text>,
      width: { minWidth: '140px', maxWidth: '120px' },
    },
  ];

  const fetchTickets = useCallback(() => {
    let isFilterActive = false;
    const payload: GetPatientAccountTicketsRequestPayload = {
      page,
    };
    if (search) {
      payload.search_text = search;
      isFilterActive = true;
    }
    if (status && status !== 0) {
      payload.status = status;
      isFilterActive = true;
    }
    if (type && type !== 0) {
      payload.type = type;
      isFilterActive = true;
    }
    if (date) {
      payload.date = formatDate(date.toISOString());
      isFilterActive = true;
    }
    setIsFilterActive(isFilterActive);
    dispatch(getPatientAccountTicketsRequest(payload));
  }, [page, search, status, type, date, dispatch]);

  // ===========================
  // side effects
  // ===========================
  useEffect(() => {
    const timeout = setTimeout(fetchTickets, 500);
    return () => clearTimeout(timeout);
  }, [fetchTickets]);

  useEffect(() => {
    if (failure) {
      if (['E-10044', 'E-10054'].includes(failure)) {
        setErrorMessage(getErrorMessage(failure));
        setIsExporting(false);
        setIsDeleting(false);
        setIsAddingHold(false);
        setIsRemovingHold(false);
        return;
      }
      if (isExporting) {
        setIsExporting(false);
      }
      if (isDeleting) {
        setIsDeleting(false);
      }
      if (isAddingHold) {
        setIsAddingHold(false);
      }
      if (isRemovingHold) {
        setIsRemovingHold(false);
      }
      showToast(getErrorMessage(failure), 'failure');
    }
    return () => {
      dispatch(resetPatientAdminMessage());
    };
  }, [failure, isExporting, isDeleting, isAddingHold, isRemovingHold, showToast, dispatch]);

  useEffect(() => {
    if (success && isExporting) {
      showToast('Patient account data exported successfully', 'success');
      handleCloseModal();
      setIsExporting(false);
      // Refresh the tickets list
      fetchTickets();
      dispatch(resetPatientAdminMessage());
    } else if (success && isDeleting) {
      showToast('Patient account deleted successfully', 'success');
      handleCloseModal();
      setIsDeleting(false);
      // Refresh the tickets list
      fetchTickets();
      dispatch(resetPatientAdminMessage());
    } else if (success && isAddingHold) {
      showToast('Compliance hold added successfully', 'success');
      handleCloseModal();
      setIsAddingHold(false);
      // Refresh the tickets list
      fetchTickets();
      dispatch(resetPatientAdminMessage());
    } else if (success && isRemovingHold) {
      showToast('Hold removed successfully', 'success');
      handleCloseModal();
      setIsRemovingHold(false);
      // Refresh the tickets list
      fetchTickets();
      dispatch(resetPatientAdminMessage());
    } else if (success) {
      dispatch(resetPatientAdminMessage());
    }
  }, [success, isExporting, isDeleting, isAddingHold, isRemovingHold, showToast, dispatch, fetchTickets]);


  return (
    <div className="p-6 flex flex-col grow gap-4">
      <div className="mb-2">
        <Text variant="body" className="!text-deep_blue !font-FigtreeMeduim !text-2xl">
          Manage patient data export and deletion requests
        </Text>
      </div>

      <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 z-[100] items-end">
        <TextInput
          value={search}
          onChangeText={setSearch}
          leftIcon="search_blue"
          className="col-span-full md:flex-1 md:min-w-[200px] md:max-w-[280px]"
          wrapperClassName="px-2 border"
          inputClassName="!pr-0"
          placeholder="Search by Name, ID"
        />
        <SelectInput
          value={status}
          onChange={value => setStatus(value.id)}
          className="w-full md:w-[160px] md:flex-shrink-0"
          itemTextClassName="text-wrap !text-sm"
          placeholder="All Status"
          wrapperClassName="border"
          options={StatusFilterOptions}
        />
        <SelectInput
          value={type}
          onChange={value => setType(value.id)}
          className="w-full md:w-[160px] md:flex-shrink-0"
          placeholder="Request Type"
          wrapperClassName="border"
          itemTextClassName="!text-sm"
          options={TypeFilterOptions}
        />

        <DateInput
          value={date}
          onChangeDate={setDate}
          placeholder="Date"
          labelClassName="!font-FigtreeSemiBold"
          wrapperClassName="border rounded-lg"
          className="w-full md:w-[170px] md:flex-shrink-0"
          textClassName="text-start text-ui_blue"
          maxValue={new Date()}
        />

        {isFilterActive && (
          <Button title="Reset Filters" variant="secondary" onClick={resetFilter} className="w-full md:w-auto md:flex-shrink-0 md:whitespace-nowrap" />
        )}
      </div>

      <DataTable
        columns={columns}
        data={tickets}
        totalPages={totalPages}
        currentPage={currentPage}
        totalResult={totalResults}
        onPageChange={setPage}
        loading={isLoading}
      />

      {/* {selectedTicket && <PatientAccountTicketActionModal 
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        ticket={selectedTicket}
        onExport={handleExport}
        onFinalDeletion={handleFinalDeletion}
        onAddComplianceHold={handleAddComplianceHold}
        onRemoveHold={handleRemoveHold}
        onApproveDeletion={handleApproveDeletion}
      />} */}

      {/* Request Details Modal */}
      {selectedTicket?.status.id === PatientAccountTicketRequestStatus.Requested ? (
        <RequestDetailsModal isOpen={isModalOpen} onClose={handleCloseModal} ticket={selectedTicket} onExport={handleExport} />
      ) : selectedTicket?.status.id === PatientAccountTicketRequestStatus.Exported ? (
        <ExportedRequestDetailsModal isOpen={isModalOpen} onClose={handleCloseModal} ticket={selectedTicket} />
      ) : selectedTicket?.status.id === PatientAccountTicketRequestStatus.Pending ? (
        <PendingRequestDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          ticket={selectedTicket}
          onApproveDeletion={handleExport}
          onAddComplianceHold={handleAddComplianceHold}
        />
      ) : selectedTicket?.status.id === PatientAccountTicketRequestStatus.Paused ? (
        <PausedRequestDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          ticket={selectedTicket}
          onRemoveHold={handleRemoveHold}
          onApproveDeletion={handleApproveDeletion}
        />
      ) : selectedTicket?.status.id === PatientAccountTicketRequestStatus.Resumed ? (
        <ResumedRequestDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          ticket={selectedTicket}
          onApproveDeletion={handleExport}
          onAddComplianceHold={handleAddComplianceHold}
        />
      ) : selectedTicket?.status.id === PatientAccountTicketRequestStatus.Approved ? (
        <ApprovedRequestDetailsModal isOpen={isModalOpen} onClose={handleCloseModal} ticket={selectedTicket} onFinalDeletion={handleFinalDeletion} />
      ) : selectedTicket?.status.id === PatientAccountTicketRequestStatus.Deleted ? (
        <DeletedRequestDetailsModal isOpen={isModalOpen} onClose={handleCloseModal} ticket={selectedTicket} />
      ) : selectedTicket?.status.id === PatientAccountTicketRequestStatus.Cancelled ? (
        <CancelledRequestDetailsModal isOpen={isModalOpen} onClose={handleCloseModal} ticket={selectedTicket} />
      ) : null}
    </div>
  );
};
